// App Banners. The page keeps the state; the forms and tables are under components/banners.
//
//   Personalized   a message pinned to people's home page
//   Task activity  a to-do pinned there until it is done
//   Sent history   everything sent on this project, each send's tracker, and pulling a
//                  live banner back
//   Test pushes    the four test fires at one device
//
// The open tab is in the address (?tab=), read on every render, so a link, a reload and
// Back all agree. What has been typed lives above the tabs, so moving between them keeps it.

import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BellRing, Download, History, ListChecks, Loader2, Megaphone, RefreshCw, Send, X } from 'lucide-react';
import MasterTabStrip, { type MasterTab } from '../components/MasterTabStrip';
import { InlineLoader } from '../components/loading';
import { BannerTracker } from '../components/banners/BannerTracker';
import { PersonalizedForm } from '../components/banners/PersonalizedForm';
import { useAuthStore } from '../stores/authStore';
import { PhonePreview } from '../components/banners/PhonePreview';
import { PushLogRow } from '../components/banners/RecentPushes';
import { SentHistoryTab } from '../components/banners/SentHistoryTab';
import { TaskForm } from '../components/banners/TaskForm';
import { TestPushesTab } from '../components/banners/TestPushesTab';
import { DEFAULT_TASK_DAYS, DURATIONS, type BannerTab } from '../components/banners/bannerOptions';
import { tokensAsLabels } from '../components/banners/bannerTokens';
import type { PushLogEntry } from '../components/banners/pushLog';
import { useBannerComposer, type BannerComposer } from '../components/banners/useBannerComposer';
import { trackerShowFrom, useBannerTracker } from '../components/banners/useBannerTracker';
import { useSentHistory } from '../components/banners/useSentHistory';
import { useTestPushes } from '../components/banners/useTestPushes';

// Only when asked for: it is the whole Tasks page.
const BannerFormsView = lazy(() => import('../components/banners/BannerFormsView'));

const TABS: MasterTab<BannerTab>[] = [
  { key: 'personalized', label: 'Personalized', icon: Megaphone },
  { key: 'task', label: 'Task activity', icon: ListChecks },
  { key: 'history', label: 'Sent history', icon: History },
  { key: 'test', label: 'Test pushes', icon: BellRing },
];

/** An address with no tab, or one this page has not got, opens Personalized. */
const tabFromAddress = (value: string | null): BannerTab =>
  TABS.find((t) => t.key === value)?.key ?? 'personalized';

/** What the panel's (i) says about a banner: how it behaves once it is on someone's phone. */
const howItWorks = (c: BannerComposer): string => {
  // A serious card, and one with no decoration, have no particles to speak of.
  const particles = c.annDecoration !== 'none' && !(c.isTask && c.taskType === 'consent') ? ' Particles animate on the device.' : '';
  if (c.isTask) {
    const { appNote } = c.taskDef;
    const ctaLabel = c.buttons.secondary ? null : c.buttons.primary;
    const aButton = ctaLabel ? `${/^[aeiou]/i.test(ctaLabel) ? 'an' : 'a'} ${ctaLabel} button` : '';
    const lasts = c.completeBy
      ? c.afterDue === 'take_off'
        ? 'at the Complete by date'
        : `after at most ${DEFAULT_TASK_DAYS} days, showing when it was due`
      : `after at most ${DEFAULT_TASK_DAYS} days`;
    return (
      `Each person gets a card pinned to the top of their app home page${
        c.buttons.secondary ? ' with two buttons' : ctaLabel ? ` with ${aButton}` : ''
      }` +
      `${c.taskSendPush ? ', plus a push' : ''}. They cannot close it. It goes on its own ` +
      `${c.doneWhen ? `once ${c.doneWhen}, ` : ''}${lasts}, ` +
      `or when you remove it from Sent history, which also opens who has seen it. ` +
      `${appNote(c.taskSendPush, c.form.picked?.bannerOnly === true)}${particles}`
    );
  }
  const howLong = c.countdownDrivesVisibility
    ? 'until the countdown ends'
    : `for ${DURATIONS.find((d) => d.days === c.annVisibleDays)?.label ?? `${c.annVisibleDays} days`}`;
  return (
    `Each person gets a push and a card pinned to the top of their app home page ${howLong}. ` +
    `They cannot close it; remove it early from Sent history. Tapping it opens a full-screen version.${particles} ` +
    'New wording, images, decorations and colours never need an app release.'
  );
};

export default function PushTestPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromAddress(searchParams.get('tab'));
  // One send's tracker is in the address too (&send=, &show=), so Back leaves it.
  const trackerBatch = tab === 'history' ? searchParams.get('send') : null;
  const trackerShow = trackerShowFrom(searchParams.get('show'));
  // Task activity's Banner-only forms, reached from Fill a form (&forms=1), so Back leaves it.
  const bannerForms = tab === 'task' && searchParams.get('forms') === '1';
  // A new entry in the history, so Back returns to the tab before. The open tab's own name
  // does something only from a tracker or the Banner-only forms: it goes back to that tab's list.
  const selectTab = (next: BannerTab) => {
    if (next === tab && !trackerBatch && !bannerForms) return;
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    params.delete('send');
    params.delete('show');
    params.delete('forms');
    setSearchParams(params);
  };
  const openTracker = (batchId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('send', batchId);
    params.delete('show');
    setSearchParams(params);
  };

  const [sending, setSending] = useState(false);
  const [log, setLog] = useState<PushLogEntry[]>([]);
  // The outcome of the last send or removal, under the button on the tab it came from.
  const [outcome, setOutcome] = useState<{ entry: PushLogEntry; from: BannerTab } | null>(null);
  const report = (entry: PushLogEntry, from: BannerTab) => {
    setLog((prev) => [entry, ...prev]);
    // Test pushes has the whole log on screen beside the form.
    setOutcome(from === 'test' ? null : { entry, from });
  };

  // The list is not on screen behind a tracker: coming back from one reads it again.
  const sent = useSentHistory({ open: tab === 'history' && !trackerBatch, report });
  const composer = useBannerComposer({ tab, sending, setSending, report, onSent: sent.fetchHistory });
  const test = useTestPushes({ setSending, report });
  const tokens = composer.audience.details.tokens;
  const tracker = useBannerTracker(trackerBatch, trackerShow, tokens);
  const trackedSend = tracker.tracker?.send;
  const removeTrackedSend = async () => {
    if (!tracker.tracker || !trackedSend) return;
    await sent.revokeBatch({
      ...trackedSend,
      title: tokensAsLabels(trackedSend.title, tokens),
      recipients: tracker.tracker.people.length,
    });
    tracker.reload();
  };

  // Back from the Banner-only forms: a form made there is on Fill a form's list.
  const reloadForms = composer.form.reload;
  const wasOnForms = useRef(false);
  useEffect(() => {
    if (wasOnForms.current && !bannerForms) reloadForms();
    wasOnForms.current = bannerForms;
    // Only the move back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerForms]);

  // A tracker is one project's: another project goes back to its list. Not a new entry in the
  // history, as Back would only come round to the send again.
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const projectOnScreen = useRef(selectedProjectId);
  useEffect(() => {
    if (projectOnScreen.current === selectedProjectId) return;
    projectOnScreen.current = selectedProjectId;
    if (!searchParams.has('send') && !searchParams.has('show')) return;
    const params = new URLSearchParams(searchParams);
    params.delete('send');
    params.delete('show');
    setSearchParams(params, { replace: true });
    // Only a change of project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  const isComposer = tab === 'personalized' || tab === 'task';
  const canSend = tab === 'test' ? test.canSend : composer.canSend;
  const sendNow = tab === 'test' ? test.send : composer.send;
  const sendLabel = tab === 'test' ? (sending ? 'Sending...' : 'Send Push') : composer.sendLabel;
  const shownOutcome = outcome?.from === tab ? outcome.entry : null;

  return (
    <div className={`space-y-6 ${tab === 'history' ? '' : 'pb-24 lg:pb-0'}`}>
      <div>
        <h1 className="text-2xl font-bold text-gray-900">App Banners</h1>
        <p className="mt-1 text-sm text-gray-500">
          Send a message or a to-do that pins as a banner on employees' app home screen.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <MasterTabStrip tabs={TABS} active={tab} onSelect={selectTab} />

        {/* Top right on a laptop. On a phone the form is long and this is what it is all
            for, so Send sits in a bar along the bottom of the screen instead. */}
        {!bannerForms && <div
          className={
            tab === 'history'
              ? 'relative ml-auto flex flex-wrap items-center justify-end gap-3'
              : 'fixed inset-x-0 bottom-0 z-20 flex flex-col gap-2 border-t border-gray-200 bg-white px-4 py-3 lg:relative lg:inset-auto lg:z-auto lg:ml-auto lg:flex-row lg:items-center lg:border-0 lg:bg-transparent lg:p-0'
          }
        >
          {trackerBatch ? (
            // Frame 8: what can be done with the send, in Send's place.
            <>
              {!tracker.notFound && !tracker.foreignProject && (
                <button
                  type="button"
                  onClick={tracker.downloadExcel}
                  disabled={tracker.rows.length === 0}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Download Excel
                </button>
              )}
              {trackedSend?.active && !trackedSend.revokedAt && !tracker.foreignProject && (
                <button
                  type="button"
                  onClick={removeTrackedSend}
                  disabled={sent.revokingBatch === trackedSend.batchId}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary-600 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                >
                  Remove from home screens
                </button>
              )}
            </>
          ) : tab === 'history' ? (
            <button
              type="button"
              onClick={sent.fetchHistory}
              disabled={sent.historyLoading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${sent.historyLoading ? 'animate-spin' : ''}`} />
              {sent.historyLoading ? 'Loading…' : 'Refresh'}
            </button>
          ) : (
            <>
              {/* Say why the button is off, rather than leave it greyed out. */}
              {isComposer && composer.blockedReason && (
                <p className="text-center text-xs font-medium text-amber-700 lg:max-w-xs lg:text-right">
                  {composer.blockedReason}
                </p>
              )}
              <button
                type="button"
                onClick={sendNow}
                disabled={!canSend || sending}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sendLabel}
              </button>
            </>
          )}

          {shownOutcome && (
            <div
              role="status"
              className={`absolute z-30 flex max-h-[60vh] items-start overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg ${
                tab === 'history'
                  ? 'right-0 top-full mt-2 w-[calc(100vw-3rem)] sm:w-[28rem]'
                  : // As wide as the preview panel below it, so it never sits on the form.
                    'inset-x-4 bottom-full mb-2 lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-full lg:mb-0 lg:mt-2 lg:w-[336px]'
              }`}
            >
              <div className="min-w-0 flex-1">
                <PushLogRow entry={shownOutcome} />
              </div>
              <button
                type="button"
                onClick={() => setOutcome(null)}
                aria-label="Close"
                className="m-2 flex h-6 w-6 flex-none items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>}
      </div>

      {bannerForms && (
        <Suspense fallback={<InlineLoader label="Loading forms…" />}>
          <BannerFormsView />
        </Suspense>
      )}
      {isComposer && !bannerForms && (
        // On a laptop the two fill what is left of the window, so Send and the preview stay
        // on screen and only the form scrolls, inside its card. 308px: the top bar, this
        // page's heading and tabs, and the gaps between them.
        <div className="grid gap-6 lg:h-[calc(100vh-308px)] lg:max-h-[720px] lg:min-h-[320px] lg:grid-cols-[minmax(0,1fr)_336px] lg:grid-rows-[minmax(0,1fr)]">
          {/* relative: what is visually hidden inside (the file input, the read-aloud hints) must
              scroll with the card, or it stretches the page from where it sits. */}
          <section className="relative min-h-0 rounded-lg border border-gray-200 bg-white p-4 sm:p-6 lg:overflow-y-auto">
            {tab === 'task' ? <TaskForm composer={composer} /> : <PersonalizedForm composer={composer} />}
          </section>
          <PhonePreview
            title={composer.previewTitle}
            body={composer.previewBody}
            accent={composer.annAccent}
            decoration={composer.annDecoration}
            countdownTo={composer.previewCountdown}
            ctaLabel={tab === 'task' ? composer.buttons.primary : null}
            ctaArrow={composer.buttons.arrow}
            secondaryLabel={tab === 'task' ? composer.buttons.secondary : null}
            appSwitch={
              tab === 'task' && composer.buttons.hasAppSwitch
                ? { value: composer.buttons.previewApp, onChange: composer.buttons.setPreviewApp }
                : undefined
            }
            image={composer.imagePreview}
            hint={howItWorks(composer)}
            note={tab === 'task' ? composer.taskDef.previewNote : undefined}
          />
        </div>
      )}

      {tab === 'history' &&
        (trackerBatch ? (
          <BannerTracker state={tracker} tokens={tokens} />
        ) : (
          <SentHistoryTab sent={sent} onOpenTracker={openTracker} tokens={tokens} />
        ))}
      {tab === 'test' && <TestPushesTab test={test} log={log} />}
    </div>
  );
}
